import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Trophy, Swords, Flame, Video, Calendar, Users, Shield, Plus, 
  ExternalLink, ChevronRight, CheckCircle2, Clock, Play, MapPin, 
  Sparkles, X, Edit, Trash2, Award, Zap, AlertCircle, TrendingUp, 
  TrendingDown, Minus, Target, Crosshair, Star, Globe, BarChart2, 
  ThumbsUp, MessageSquare, Send, Check, ShieldAlert, Cpu
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import api from "../lib/api";

export default function Tournaments() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeMainTab = searchParams.get("view") || "matches"; // matches, ranking, players, tournaments, pickem, scrims
  const gameFilter = searchParams.get("game") || "all";

  const [tournaments, setTournaments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [players, setPlayers] = useState([]);
  const [scrims, setScrims] = useState([]);
  const [pickems, setPickems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Interactive Match Details Modal (HLTV Grade)
  const [selectedMatchDetail, setSelectedMatchDetail] = useState(null);
  const [loadingMatchDetail, setLoadingMatchDetail] = useState(false);
  const [predicting, setPredicting] = useState(false);

  // Team Analytics Drawer / Modal
  const [selectedTeamTag, setSelectedTeamTag] = useState(null);
  const [teamAnalytics, setTeamAnalytics] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Tournament details modal & Stream
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [activeStreamUrl, setActiveStreamUrl] = useState(null);
  const [tournamentBracket, setTournamentBracket] = useState(null);
  const [loadingBracket, setLoadingBracket] = useState(false);
  const [startingTourn, setStartingTourn] = useState(false);

  // Score report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingMatch, setReportingMatch] = useState(null);
  const [reportScore1, setReportScore1] = useState(16);
  const [reportScore2, setReportScore2] = useState(12);
  const [savingReport, setSavingReport] = useState(false);

  // Pick'em State
  const [pick30, setPick30] = useState("");
  const [pick03, setPick03] = useState("");
  const [pickChamp, setPickChamp] = useState("");
  const [votingPickem, setVotingPickem] = useState(false);

  // Scrim Creator Modal
  const [showCreateScrimModal, setShowCreateScrimModal] = useState(false);
  const [scrimClan, setScrimClan] = useState("");
  const [scrimTag, setScrimTag] = useState("");
  const [scrimMap, setScrimMap] = useState("de_dust2");
  const [scrimGame, setScrimGame] = useState("cs2");
  const [scrimFormat, setScrimFormat] = useState("5v5 MR12");
  const [scrimDateTime, setScrimDateTime] = useState("");
  const [scrimDiscord, setScrimDiscord] = useState("");
  const [savingScrim, setSavingScrim] = useState(false);
  
  // Admin modals
  const [showCreateTournModal, setShowCreateTournModal] = useState(false);
  const [showCreateMatchModal, setShowCreateMatchModal] = useState(false);
  const [showEditMatchModal, setShowEditMatchModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);

  // Form states - Tournament
  const [tTitle, setTTitle] = useState("");
  const [tGame, setTGame] = useState("cs2");
  const [tPrize, setTPrize] = useState("500€ + VIP Gold");
  const [tMaxTeams, setTMaxTeams] = useState(16);
  const [tFormat, setTFormat] = useState("Single Elimination (BO3)");
  const [tStartDate, setTStartDate] = useState("");
  const [tBanner, setTBanner] = useState("");
  const [tDesc, setTDesc] = useState("");
  const [tRules, setTRules] = useState("");
  const [tStream, setTStream] = useState("");
  const [savingTourn, setSavingTourn] = useState(false);
  const [syncingEsports, setSyncingEsports] = useState(false);

  // Form states - Match
  const [mTournId, setMTournId] = useState("");
  const [mGame, setMGame] = useState("cs2");
  const [mTeam1, setMTeam1] = useState("");
  const [mTeam1Tag, setMTeam1Tag] = useState("");
  const [mTeam1Score, setMTeam1Score] = useState(0);
  const [mTeam2, setMTeam2] = useState("");
  const [mTeam2Tag, setMTeam2Tag] = useState("");
  const [mTeam2Score, setMTeam2Score] = useState(0);
  const [mStatus, setMStatus] = useState("upcoming");
  const [mMap, setMMap] = useState("de_dust2");
  const [mFormat, setMFormat] = useState("BO3");
  const [mStartTime, setMStartTime] = useState("");
  const [mStream, setMStream] = useState("");
  const [savingMatch, setSavingMatch] = useState(false);

  const isStaff = user && (user.role === "root" || user.role === "admin");

  useEffect(() => {
    fetchData();
  }, [gameFilter, activeMainTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, mRes, rankRes, playRes, scrimRes, pickRes] = await Promise.all([
        api.get("/tournaments", { params: { game: gameFilter !== "all" ? gameFilter : undefined } }),
        api.get("/tournaments/matches", { params: { game: gameFilter !== "all" ? gameFilter : undefined } }),
        api.get("/hltv/ranking").catch(() => ({ data: { ranking: [] } })),
        api.get("/hltv/players").catch(() => ({ data: { players: [] } })),
        api.get("/hltv/scrims").catch(() => ({ data: { scrims: [] } })),
        api.get("/hltv/pickem").catch(() => ({ data: { challenges: [] } }))
      ]);
      setTournaments(tRes.data.tournaments || []);
      setMatches(mRes.data.matches || []);
      setRanking(rankRes.data.ranking || []);
      setPlayers(playRes.data.players || []);
      setScrims(scrimRes.data.scrims || []);
      setPickems(pickRes.data.challenges || []);
    } catch (e) {
      console.error("Error fetching tournaments & matches:", e);
      toast.error("Nu s-au putut încărca datele competițiilor.");
    } finally {
      setLoading(false);
    }
  };

  const openMatchDetail = async (matchId) => {
    setLoadingMatchDetail(true);
    try {
      const res = await api.get(`/hltv/match/${matchId}`);
      setSelectedMatchDetail(res.data);
    } catch (e) {
      toast.error("Nu s-au putut încărca statisticile meciului.");
    } finally {
      setLoadingMatchDetail(false);
    }
  };

  const openTeamDrawer = async (tag) => {
    setLoadingTeam(true);
    setSelectedTeamTag(tag);
    try {
      const res = await api.get(`/hltv/team/${tag}`);
      setTeamAnalytics(res.data);
    } catch (e) {
      toast.error("Nu s-a putut încărca profilul echipei.");
    } finally {
      setLoadingTeam(false);
    }
  };

  const handlePredict = async (teamNum) => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a vota.");
      return;
    }
    if (!selectedMatchDetail) return;
    setPredicting(true);
    try {
      const res = await api.post(`/hltv/match/${selectedMatchDetail.id}/predict`, { team: teamNum });
      toast.success(res.data.message || "Predicție salvată!");
    } catch (e) {
      toast.error("Eroare la înregistrarea predicției.");
    } finally {
      setPredicting(false);
    }
  };

  const handleVotePickem = async (challengeId) => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a participa la Pick'em.");
      return;
    }
    if (!pick30 || !pick03 || !pickChamp) {
      toast.error("Selectează toate opțiunile pentru a trimite predicția.");
      return;
    }
    setVotingPickem(true);
    try {
      const res = await api.post(`/hltv/pickem/${challengeId}/vote`, {
        pick_3_0: pick30,
        pick_0_3: pick03,
        pick_champion: pickChamp
      });
      toast.success(res.data.message || "Pick'em salvat cu succes!");
    } catch (e) {
      toast.error("Eroare la salvarea votului Pick'em.");
    } finally {
      setVotingPickem(false);
    }
  };

  const handleCreateScrim = async (e) => {
    e.preventDefault();
    if (!scrimClan.trim() || !scrimDateTime) {
      toast.error("Numele clanului și data meciului sunt obligatorii.");
      return;
    }
    setSavingScrim(true);
    try {
      await api.post("/hltv/scrims", {
        clan_name: scrimClan.trim(),
        clan_tag: scrimTag.trim() || scrimClan.substring(0, 4).toUpperCase(),
        map_preference: scrimMap,
        game: scrimGame,
        format: scrimFormat,
        date_time: scrimDateTime,
        contact_discord: scrimDiscord.trim() || "discord.gg/indungi"
      });
      toast.success("Meciul de antrenament / Scrim a fost postat!");
      setShowCreateScrimModal(false);
      fetchData();
    } catch (e) {
      toast.error("Eroare la postarea meciului.");
    } finally {
      setSavingScrim(false);
    }
  };

  const handleRegisterTeam = async (tournId) => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a înscrie o echipă.");
      return;
    }
    try {
      const res = await api.post(`/tournaments/${tournId}/register`);
      toast.success(res.data.message || "Echipa a fost înscrisă!");
      fetchData();
      if (selectedTournament && selectedTournament.id === tournId) {
        const updated = await api.get(`/tournaments/${tournId}`);
        setSelectedTournament(updated.data.tournament);
        fetchBracket(tournId);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la înscrierea echipei.");
    }
  };

  const openTournamentModal = (t) => {
    setSelectedTournament(t);
    fetchBracket(t.id);
  };

  const fetchBracket = async (tournId) => {
    setLoadingBracket(true);
    try {
      const res = await api.get(`/tournaments/${tournId}/bracket`);
      setTournamentBracket(res.data);
    } catch (e) {
      console.error("Bracket error:", e);
    } finally {
      setLoadingBracket(false);
    }
  };

  const handleStartTournament = async (tournId) => {
    setStartingTourn(true);
    try {
      const res = await api.post(`/tournaments/${tournId}/start`);
      toast.success(res.data.message || "Turneul a fost pornit!");
      fetchData();
      fetchBracket(tournId);
      const updated = await api.get(`/tournaments/${tournId}`);
      setSelectedTournament(updated.data.tournament);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la pornirea turneului.");
    } finally {
      setStartingTourn(false);
    }
  };

  const handleReportScoreSubmit = async (e) => {
    e.preventDefault();
    if (!reportingMatch) return;
    setSavingReport(true);
    try {
      const res = await api.post(`/tournaments/matches/${reportingMatch.id}/report-score`, {
        team1_score: parseInt(reportScore1),
        team2_score: parseInt(reportScore2)
      });
      toast.success(res.data.message || "Scorul a fost raportat și bracket-ul a avansat!");
      setShowReportModal(false);
      setReportingMatch(null);
      if (selectedTournament) {
        fetchBracket(selectedTournament.id);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la raportarea scorului.");
    } finally {
      setSavingReport(false);
    }
  };

  const handleCheckin = async (matchId) => {
    try {
      const res = await api.post(`/tournaments/matches/${matchId}/checkin`);
      toast.success(res.data.message || "Check-in confirmat!");
      if (selectedTournament) {
        fetchBracket(selectedTournament.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la check-in.");
    }
  };

  const handleSyncEsports = async () => {
    setSyncingEsports(true);
    try {
      const res = await api.post("/tournaments/sync-esports");
      toast.success(res.data.message || "S-au sincronizat meciurile oficiale!");
      fetchData();
    } catch (e) {
      toast.error("Eroare la sincronizarea meciurilor live.");
    } finally {
      setSyncingEsports(false);
    }
  };

  const getStreamEmbed = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    if (ytMatch) return { type: "youtube", src: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1` };
    
    const twitchMatch = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
    if (twitchMatch) {
      const channel = twitchMatch[1];
      const host = window.location.hostname || "localhost";
      return { type: "twitch", src: `https://player.twitch.tv/?channel=${channel}&parent=${host}&autoplay=true&muted=false` };
    }
    return null;
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    if (!tTitle.trim() || !tStartDate) {
      toast.error("Titlul și data de începere sunt obligatorii.");
      return;
    }
    setSavingTourn(true);
    try {
      await api.post("/tournaments", {
        title: tTitle.trim(),
        game: tGame,
        prize_pool: tPrize.trim(),
        max_teams: parseInt(tMaxTeams, 10),
        format: tFormat,
        start_date: tStartDate,
        banner_url: tBanner.trim(),
        description: tDesc.trim(),
        rules: tRules.trim(),
        stream_url: tStream.trim()
      });
      toast.success("Turneul a fost creat cu succes!");
      setShowCreateTournModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la crearea turneului.");
    } finally {
      setSavingTourn(false);
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    if (!mTeam1.trim() || !mTeam2.trim()) {
      toast.error("Numele ambelor echipe sunt obligatorii.");
      return;
    }
    setSavingMatch(true);
    try {
      await api.post("/tournaments/matches", {
        tournament_id: mTournId || undefined,
        game: mGame,
        team1_name: mTeam1.trim(),
        team1_tag: mTeam1Tag.trim() || mTeam1.trim().substring(0, 4).toUpperCase(),
        team1_score: parseInt(mTeam1Score, 10) || 0,
        team2_name: mTeam2.trim(),
        team2_tag: mTeam2Tag.trim() || mTeam2.trim().substring(0, 4).toUpperCase(),
        team2_score: parseInt(mTeam2Score, 10) || 0,
        status: mStatus,
        map_name: mMap,
        format: mFormat,
        start_time: mStartTime || new Date().toISOString(),
        stream_url: mStream.trim()
      });
      toast.success("Meciul a fost programat!");
      setShowCreateMatchModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la programarea meciului.");
    } finally {
      setSavingMatch(false);
    }
  };

  const handleUpdateMatchScore = async (e) => {
    e.preventDefault();
    if (!editingMatch) return;
    try {
      await api.patch(`/tournaments/matches/${editingMatch.id}`, {
        team1_score: parseInt(editingMatch.team1_score, 10),
        team2_score: parseInt(editingMatch.team2_score, 10),
        status: editingMatch.status,
        map_name: editingMatch.map_name,
        stream_url: editingMatch.stream_url
      });
      toast.success("Scorul meciului a fost actualizat!");
      setShowEditMatchModal(false);
      setEditingMatch(null);
      fetchData();
    } catch (e) {
      toast.error("Eroare la actualizarea scorului.");
    }
  };

  const handleDeleteMatch = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți acest meci?")) return;
    try {
      await api.delete(`/tournaments/matches/${id}`);
      toast.success("Meciul a fost șters.");
      fetchData();
    } catch (e) {
      toast.error("Eroare la ștergerea meciului.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0e14] text-white py-6 px-4 md:px-8">
      {/* Background ambient lighting */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-10 right-1/4 w-96 h-96 bg-[var(--tf-primary)]/10 rounded-full blur-[140px]" />
        <div className="absolute top-1/2 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto space-y-8">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono text-[var(--tf-primary)] tracking-wider uppercase mb-1">
              <Trophy className="w-3.5 h-3.5" />
              <span>INDUNGI HLTV ESPORTS & MATCH CENTER HUB</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight text-white flex items-center gap-3">
              <Swords className="w-8 h-8 text-[var(--tf-primary)]" />
              ESPORTS, MECIURI & TURNEE
            </h1>
            <p className="text-xs md:text-sm text-zinc-400 mt-1 max-w-2xl">
              Platformă integrată HLTV Pro & Community: Match Center cu Vetoes, Voturi Predicții, Top 30 World Team Ranking, Analiză Jucători, Scrims 5v5 și Pick'em Challenge.
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleSyncEsports}
              disabled={syncingEsports}
              className="px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 transition-all flex items-center gap-1.5 shadow-lg disabled:opacity-50"
            >
              <Zap className={`w-4 h-4 ${syncingEsports ? "animate-spin" : ""}`} />
              {syncingEsports ? "Se sincronizează..." : "Sincronizează HLTV Live"}
            </button>

            {isStaff && (
              <>
                <button
                  onClick={() => setShowCreateMatchModal(true)}
                  className="px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/15 text-white hover:bg-white/20 transition-all flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" />
                  Programează Meci
                </button>
                <button
                  onClick={() => setShowCreateTournModal(true)}
                  className="tf-btn-primary px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black hover:opacity-90 transition-all flex items-center gap-1.5 shadow-lg shadow-[var(--tf-primary)]/20"
                >
                  <Trophy className="w-4 h-4" />
                  Creează Turneu
                </button>
              </>
            )}
          </div>
        </div>

        {/* MAIN NAVIGATION TABS */}
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3">
          {[
            { id: "matches", label: "Match Center & Live Arenas", icon: Flame, badge: matches.filter(m => m.status === "live").length ? "LIVE" : null },
            { id: "ranking", label: "Top 30 HLTV World Ranking", icon: Globe, badge: "OFICIAL" },
            { id: "players", label: "Top Jucători (Rating 2.0)", icon: Target, badge: null },
            { id: "pickem", label: "Pick'em Challenge (Predicții)", icon: Award, badge: "500 PTS" },
            { id: "scrims", label: "Clan Wars & Scrims 5v5", icon: Swords, badge: scrims.length },
            { id: "tournaments", label: "Campionate Comunitate", icon: Trophy, badge: tournaments.length },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeMainTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setSearchParams({ view: tab.id, game: gameFilter })}
                className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                  isActive
                    ? "bg-[var(--tf-primary)] text-black shadow-lg shadow-[var(--tf-primary)]/20"
                    : "bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={`px-1.5 py-0.2 text-[9px] rounded-full font-black ${
                    tab.badge === "LIVE" 
                      ? "bg-red-600 text-white animate-pulse" 
                      : (isActive ? "bg-black/40 text-black" : "bg-[var(--tf-primary)]/20 text-[var(--tf-primary)]")
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* VIEW 1: MATCH CENTER & LIVE STREAMS */}
        {activeMainTab === "matches" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-black text-white flex items-center gap-2 tracking-wider uppercase">
                <Flame className="w-4 h-4 text-red-500 fill-red-500 animate-pulse" />
                Meciuri Oficiale CS2 (Apasă pe orice meci pentru Veto & Scoruri Live)
              </h2>
              <span className="text-xs text-zinc-400 font-mono">Sincronizat via HLTV & Liquipedia Circuit</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.map(m => {
                const isLive = m.status === "live";
                const isFinished = m.status === "finished";
                return (
                  <div
                    key={m.id}
                    className={`relative rounded-xl p-4 border transition-all overflow-hidden flex flex-col justify-between space-y-4 cursor-pointer group ${
                      isLive 
                        ? "bg-gradient-to-b from-red-950/40 via-zinc-950 to-zinc-950 border-red-500/50 shadow-lg shadow-red-500/10 hover:border-red-400" 
                        : "bg-zinc-950/80 border-white/10 hover:border-[var(--tf-primary)]/40"
                    }`}
                    onClick={() => openMatchDetail(m.id)}
                  >
                    {/* Top Status Bar */}
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-2">
                        {isLive ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-red-600 text-white flex items-center gap-1.5 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                            LIVE NOW
                          </span>
                        ) : isFinished ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-800 text-zinc-400">
                            FINALIZAT
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-950/80 text-cyan-400 border border-cyan-500/30 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(m.start_time).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                        <span className="text-zinc-400 font-bold uppercase">{m.game.toUpperCase()}</span>
                      </div>

                      <span className="text-zinc-400 font-mono text-[10px] truncate max-w-[140px]">{m.tournament_name || "HLTV Pro Circuit"}</span>
                    </div>

                    {/* Team VS Display */}
                    <div className="grid grid-cols-7 items-center gap-2 py-1">
                      {/* Team 1 */}
                      <div className="col-span-3 flex items-center gap-2.5">
                        <img
                          src={m.team1_logo || `https://api.dicebear.com/7.x/identicon/svg?seed=${m.team1_name}`}
                          alt={m.team1_name}
                          className="w-9 h-9 rounded-lg bg-black/60 border border-white/10 p-1 object-contain"
                        />
                        <div className="overflow-hidden">
                          <p className="text-xs font-black text-white truncate leading-tight group-hover:text-[var(--tf-primary)] transition-colors">{m.team1_name}</p>
                          <span className="text-[10px] font-mono text-zinc-500">[{m.team1_tag}]</span>
                        </div>
                      </div>

                      {/* Score / VS Center */}
                      <div className="col-span-1 text-center">
                        {isLive || isFinished ? (
                          <div className="flex items-center justify-center gap-1 text-sm font-black font-mono">
                            <span className={m.team1_score > m.team2_score ? "text-[var(--tf-primary)]" : "text-white"}>{m.team1_score}</span>
                            <span className="text-zinc-600">:</span>
                            <span className={m.team2_score > m.team1_score ? "text-[var(--tf-primary)]" : "text-white"}>{m.team2_score}</span>
                          </div>
                        ) : (
                          <span className="text-xs font-black text-zinc-600 font-mono">VS</span>
                        )}
                      </div>

                      {/* Team 2 */}
                      <div className="col-span-3 flex items-center justify-end gap-2.5 text-right">
                        <div className="overflow-hidden">
                          <p className="text-xs font-black text-white truncate leading-tight group-hover:text-[var(--tf-primary)] transition-colors">{m.team2_name}</p>
                          <span className="text-[10px] font-mono text-zinc-500">[{m.team2_tag}]</span>
                        </div>
                        <img
                          src={m.team2_logo || `https://api.dicebear.com/7.x/identicon/svg?seed=${m.team2_name}`}
                          alt={m.team2_name}
                          className="w-9 h-9 rounded-lg bg-black/60 border border-white/10 p-1 object-contain"
                        />
                      </div>
                    </div>

                    {/* Bottom Details & Stream Action */}
                    <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px]" onClick={e => e.stopPropagation()}>
                      <span className="text-zinc-400 font-medium">
                        Harta: <strong className="text-zinc-200">{m.map_name || "de_dust2"}</strong> ({m.format})
                      </span>

                      <div className="flex items-center gap-2">
                        {m.stream_url && (
                          <button
                            onClick={() => setActiveStreamUrl(m.stream_url)}
                            className="px-2.5 py-1 rounded bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 transition-all"
                          >
                            <Play className="w-2.5 h-2.5 fill-red-400" />
                            Stream
                          </button>
                        )}

                        <button
                          onClick={() => openMatchDetail(m.id)}
                          className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-[10px] font-bold text-zinc-300 flex items-center gap-1"
                        >
                          <BarChart2 className="w-3 h-3 text-[var(--tf-primary)]" />
                          Veto & Stats
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* VIEW 2: HLTV TOP 30 WORLD RANKING */}
        {activeMainTab === "ranking" && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-black text-white flex items-center gap-2 uppercase tracking-wide">
                  <Globe className="w-5 h-5 text-[var(--tf-primary)]" />
                  HLTV.org Official World Team Ranking
                </h2>
                <p className="text-xs text-zinc-400">Apasă pe orice echipă pentru statistici de win-rate pe hărți, trofee și detalii de jucători.</p>
              </div>
            </div>

            <div className="bg-zinc-950/80 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="divide-y divide-white/5">
                {ranking.map((team) => {
                  const isTop3 = team.rank <= 3;
                  return (
                    <div
                      key={team.rank}
                      onClick={() => openTeamDrawer(team.tag)}
                      className={`p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-colors hover:bg-white/5 cursor-pointer ${
                        isTop3 ? "bg-[var(--tf-primary)]/5" : ""
                      }`}
                    >
                      {/* Left: Rank & Team Info */}
                      <div className="flex items-center gap-4">
                        <div className="w-10 text-center">
                          <span className={`text-xl font-black font-mono ${
                            team.rank === 1 ? "text-amber-400" : team.rank === 2 ? "text-zinc-300" : team.rank === 3 ? "text-amber-600" : "text-zinc-500"
                          }`}>
                            #{team.rank}
                          </span>
                          
                          <div className="flex items-center justify-center text-[10px] font-bold">
                            {team.change > 0 ? (
                              <span className="text-emerald-400 flex items-center"><TrendingUp className="w-2.5 h-2.5 mr-0.5" />+{team.change}</span>
                            ) : team.change < 0 ? (
                              <span className="text-red-400 flex items-center"><TrendingDown className="w-2.5 h-2.5 mr-0.5" />{team.change}</span>
                            ) : (
                              <span className="text-zinc-600"><Minus className="w-2.5 h-2.5" /></span>
                            )}
                          </div>
                        </div>

                        <img
                          src={team.logo}
                          alt={team.team_name}
                          className="w-12 h-12 rounded-xl bg-black/60 border border-white/10 p-1.5 object-contain"
                        />

                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-black text-white hover:text-[var(--tf-primary)] transition-colors">
                              {team.team_name}
                            </h3>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-white/5 text-zinc-400">
                              [{team.tag}]
                            </span>
                          </div>
                          <p className="text-xs text-zinc-400 mt-0.5">{team.country}</p>
                        </div>
                      </div>

                      {/* Middle: Lineup */}
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        {team.roster?.map((player, idx) => (
                          <span key={idx} className="px-2.5 py-1 rounded-lg bg-black/60 border border-white/5 text-zinc-300 font-bold">
                            {player}
                          </span>
                        ))}
                        {team.coach && (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-300 font-mono">
                            C: {team.coach}
                          </span>
                        )}
                      </div>

                      {/* Right: Points */}
                      <div className="text-right">
                        <span className="text-sm font-black text-[var(--tf-primary)] font-mono block">
                          {team.points} pts
                        </span>
                        <span className="text-[10px] text-zinc-500 uppercase font-bold">HLTV Points</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* VIEW 3: TOP PLAYERS HLTV RATING 2.0 */}
        {activeMainTab === "players" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-black text-white flex items-center gap-2 uppercase tracking-wide">
                <Target className="w-5 h-5 text-[var(--tf-primary)]" />
                Top Jucători Profesioniști (HLTV Rating 2.0)
              </h2>
              <p className="text-xs text-zinc-400">Statistici oficiale de performanță individuală la cel mai înalt nivel de CS2.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {players.map((p, idx) => (
                <div
                  key={p.id}
                  className="bg-zinc-950/80 border border-white/15 rounded-2xl p-6 relative overflow-hidden shadow-xl hover:border-[var(--tf-primary)]/50 transition-all group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img src={p.avatar} alt={p.nickname} className="w-12 h-12 rounded-xl bg-black/60 border border-white/10 p-1" />
                      <div>
                        <h3 className="text-lg font-black text-white group-hover:text-[var(--tf-primary)] transition-colors">
                          {p.nickname}
                        </h3>
                        <p className="text-xs text-zinc-400 truncate">{p.name}</p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-xl font-black text-[var(--tf-primary)] font-mono block">
                        {p.rating_2_0}
                      </span>
                      <span className="text-[9px] text-zinc-500 uppercase font-bold">Rating 2.0</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs py-2 border-y border-white/5 mb-4 text-zinc-300">
                    <div className="flex items-center gap-1.5">
                      <img src={p.team_logo} alt={p.team} className="w-4 h-4 object-contain" />
                      <span className="font-bold">{p.team}</span>
                    </div>
                    <span className="text-zinc-500 font-mono text-[11px]">{p.country}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-white/5 rounded-lg p-2">
                      <span className="block text-[9px] text-zinc-500 uppercase font-bold">ADR</span>
                      <span className="font-bold font-mono text-white">{p.adr}</span>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <span className="block text-[9px] text-zinc-500 uppercase font-bold">KAST</span>
                      <span className="font-bold font-mono text-white">{p.kast}</span>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <span className="block text-[9px] text-zinc-500 uppercase font-bold">HS %</span>
                      <span className="font-bold font-mono text-white">{p.headshot_pct}</span>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 text-[11px] flex items-center justify-between text-zinc-400">
                    <span>Armă Favorită:</span>
                    <strong className="text-white">{p.favorite_weapon}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* VIEW 4: PICK'EM CHALLENGE (PREDICTIONS) */}
        {activeMainTab === "pickem" && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-white flex items-center gap-2 uppercase tracking-wide">
                  <Award className="w-6 h-6 text-amber-400" />
                  INDUNGI Major Pick'em Challenge
                </h2>
                <p className="text-xs text-zinc-400">Alege echipele câștigătoare și adună puncte comunitare + Medalia de Aur Pick'em pe profilul tău!</p>
              </div>
            </div>

            {pickems.map(p => (
              <div key={p.id} className="bg-zinc-950/90 border border-amber-500/30 rounded-2xl p-6 md:p-8 space-y-6 shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">PREMII: 500 PUNCTE FORUM & MEDALIE</span>
                    <h3 className="text-xl font-black text-white">{p.title}</h3>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    Înscrieri Deschise
                  </span>
                </div>

                <p className="text-xs text-zinc-300">{p.description}</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Pick 3-0 */}
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-3">
                    <span className="text-xs font-black text-emerald-400 uppercase flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4" /> Echipa 3-0 (Neînvinsă)
                    </span>
                    <select
                      value={pick30}
                      onChange={e => setPick30(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/15 rounded-lg p-2.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                    >
                      <option value="">Alege o echipă...</option>
                      {p.options_3_0?.map((t, idx) => (
                        <option key={idx} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Pick 0-3 */}
                  <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-3">
                    <span className="text-xs font-black text-red-400 uppercase flex items-center gap-1.5">
                      <ShieldAlert className="w-4 h-4" /> Echipa 0-3 (Eliminată)
                    </span>
                    <select
                      value={pick03}
                      onChange={e => setPick03(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/15 rounded-lg p-2.5 text-xs text-white outline-none focus:border-[var(--tf-primary)]"
                    >
                      <option value="">Alege o echipă...</option>
                      {p.options_0_3?.map((t, idx) => (
                        <option key={idx} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Pick Champion */}
                  <div className="bg-black/50 border border-amber-500/30 rounded-xl p-4 space-y-3">
                    <span className="text-xs font-black text-amber-400 uppercase flex items-center gap-1.5">
                      <Trophy className="w-4 h-4" /> Campioana Turneului
                    </span>
                    <select
                      value={pickChamp}
                      onChange={e => setPickChamp(e.target.value)}
                      className="w-full bg-zinc-900 border border-amber-500/40 rounded-lg p-2.5 text-xs text-amber-300 font-bold outline-none focus:border-amber-400"
                    >
                      <option value="">Alege Campioana...</option>
                      {p.options_champion?.map((t, idx) => (
                        <option key={idx} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleVotePickem(p.id)}
                    disabled={votingPickem}
                    className="tf-btn-primary px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-amber-400 text-black hover:bg-amber-300 shadow-lg shadow-amber-400/20"
                  >
                    {votingPickem ? "Se trimite..." : "Trimite Predicția Pick'em"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* VIEW 5: SCRIMS & CLAN WARS FINDER */}
        {activeMainTab === "scrims" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-white flex items-center gap-2 uppercase tracking-wide">
                  <Swords className="w-6 h-6 text-[var(--tf-primary)]" />
                  Clan Wars & Scrims 5v5 Finder
                </h2>
                <p className="text-xs text-zinc-400">Găsește meciuri de antrenament și clan war pe serverele oficiale de 128-tick INDUNGI.</p>
              </div>

              <button
                onClick={() => setShowCreateScrimModal(true)}
                className="tf-btn-primary px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black hover:opacity-90 flex items-center gap-1.5 shadow-lg shadow-[var(--tf-primary)]/20"
              >
                <Plus className="w-4 h-4" />
                Postează Meci 5v5
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {scrims.map(s => (
                <div key={s.id} className="bg-zinc-950/80 border border-white/10 rounded-2xl p-5 space-y-4 hover:border-white/25 transition-all shadow-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/30 flex items-center justify-center font-black text-sm text-[var(--tf-primary)]">
                        {s.clan_tag}
                      </div>
                      <div>
                        <h4 className="text-base font-black text-white">{s.clan_name}</h4>
                        <span className="text-[10px] text-zinc-400 font-mono">{s.format} • {s.game.toUpperCase()}</span>
                      </div>
                    </div>

                    <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      Disponibil
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white/5 rounded-lg p-2">
                      <span className="block text-[9px] text-zinc-500 uppercase font-bold">Hartă Preferată</span>
                      <span className="font-bold text-white">{s.map_preference}</span>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2">
                      <span className="block text-[9px] text-zinc-500 uppercase font-bold">Data & Ora</span>
                      <span className="font-bold text-white">{new Date(s.date_time).toLocaleDateString("ro-RO")} {new Date(s.date_time).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/5 flex items-center justify-between text-xs">
                    <span className="text-zinc-400 text-[11px]">Server: <code className="text-[var(--tf-primary)]">{s.server_assigned}</code></span>
                    <a
                      href="https://discord.gg/CrGC77X8W"
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 border border-indigo-500/40 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Contact Discord
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* VIEW 6: TOURNAMENTS SECTION */}
        {activeMainTab === "tournaments" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="text-xl font-black text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-[var(--tf-primary)]" />
                Turnee & Campionate Oficiale INDUNGI
              </h2>

              <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg p-1">
                {[
                  { id: "all", label: "Toate Jocurile" },
                  { id: "cs2", label: "Counter-Strike 2" },
                  { id: "cs16", label: "CS 1.6 Clasic" },
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setSearchParams({ view: "tournaments", game: t.id === "all" ? "" : t.id })}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                      gameFilter === t.id
                        ? "bg-[var(--tf-primary)] text-black font-black"
                        : "text-zinc-400 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {tournaments.map(t => {
                const enrolledCount = t.enrolled_teams ? t.enrolled_teams.length : 0;
                const isRegOpen = t.status === "registration";
                return (
                  <div
                    key={t.id}
                    className="rounded-2xl overflow-hidden border border-white/15 bg-zinc-950/80 hover:border-[var(--tf-primary)]/50 transition-all duration-300 flex flex-col justify-between shadow-xl group"
                  >
                    <div>
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={t.banner_url || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=80"}
                          alt={t.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 brightness-75"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />

                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black">
                            {t.game.toUpperCase()}
                          </span>
                          <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-black/80 backdrop-blur-md border border-white/15 text-amber-400">
                            💰 {t.prize_pool}
                          </span>
                        </div>

                        <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                          isRegOpen ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                        }`}>
                          {isRegOpen ? "Înscrieri Deschise" : "În Desfășurare"}
                        </span>
                      </div>

                      <div className="p-6 space-y-3">
                        <h3 className="text-xl font-black text-white leading-tight group-hover:text-[var(--tf-primary)] transition-colors">
                          {t.title}
                        </h3>

                        <p className="text-xs text-zinc-300 line-clamp-2 leading-relaxed">
                          {t.description}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 text-xs">
                          <div className="bg-white/5 border border-white/5 rounded-lg p-2.5">
                            <span className="block text-[10px] text-zinc-500 font-bold uppercase">Format</span>
                            <span className="font-bold text-white text-xs truncate block">{t.format}</span>
                          </div>
                          <div className="bg-white/5 border border-white/5 rounded-lg p-2.5">
                            <span className="block text-[10px] text-zinc-500 font-bold uppercase">Echipe Înscrise</span>
                            <span className="font-bold text-white text-xs">{enrolledCount} / {t.max_teams}</span>
                          </div>
                          <div className="bg-white/5 border border-white/5 rounded-lg p-2.5 col-span-2 sm:col-span-1">
                            <span className="block text-[10px] text-zinc-500 font-bold uppercase">Data Start</span>
                            <span className="font-bold text-white text-xs">{new Date(t.start_date).toLocaleDateString("ro-RO")}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 pt-0 flex items-center justify-between gap-3">
                      <button
                        onClick={() => openTournamentModal(t)}
                        className="px-4 py-2.5 rounded-lg text-xs font-bold bg-white/10 hover:bg-white/20 text-white border border-white/15 transition-all flex items-center gap-1.5"
                      >
                        <Trophy className="w-3.5 h-3.5 text-[var(--tf-primary)]" />
                        Detalii & Bracket Real
                      </button>

                      {isRegOpen && (
                        <button
                          onClick={() => handleRegisterTeam(t.id)}
                          className="tf-btn-primary px-4 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black hover:opacity-90 transition-all flex items-center gap-1.5 shadow-lg shadow-[var(--tf-primary)]/20"
                        >
                          <Shield className="w-3.5 h-3.5" />
                          Înscrie Echipa
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* 🔴 HLTV-GRADE MATCH DETAIL MODAL */}
      {selectedMatchDetail && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 md:p-6 overflow-y-auto">
          <div className="relative w-full max-w-5xl bg-zinc-950 border border-white/20 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6 my-auto max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <span className="text-[11px] font-mono text-[var(--tf-primary)] font-bold uppercase tracking-wider">
                  HLTV PRO MATCH CENTER • {selectedMatchDetail.event}
                </span>
                <h2 className="text-2xl font-black text-white flex items-center gap-3 mt-1">
                  <span>{selectedMatchDetail.team1?.name}</span>
                  <span className="text-amber-400 font-mono text-xl">vs</span>
                  <span>{selectedMatchDetail.team2?.name}</span>
                </h2>
              </div>
              <button onClick={() => setSelectedMatchDetail(null)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Score & Prediction Bar */}
            <div className="bg-black/60 border border-white/10 rounded-xl p-6 space-y-4">
              <div className="grid grid-cols-7 items-center gap-4 text-center">
                <div className="col-span-3 flex items-center justify-center gap-3">
                  <img src={selectedMatchDetail.team1?.logo} alt="t1" className="w-12 h-12 p-1.5 bg-zinc-900 rounded-xl" />
                  <div className="text-left">
                    <h3 className="text-lg font-black text-white">{selectedMatchDetail.team1?.name}</h3>
                    <span className="text-xs text-zinc-500 font-mono">Rank #{selectedMatchDetail.team1?.rank}</span>
                  </div>
                </div>

                <div className="col-span-1">
                  <span className="text-3xl font-black font-mono text-[var(--tf-primary)]">
                    {selectedMatchDetail.team1?.score} : {selectedMatchDetail.team2?.score}
                  </span>
                  <span className="text-[10px] text-red-400 block font-bold uppercase animate-pulse mt-0.5">
                    {selectedMatchDetail.status === "live" ? "🔴 LIVE" : selectedMatchDetail.status}
                  </span>
                </div>

                <div className="col-span-3 flex items-center justify-center gap-3">
                  <div className="text-right">
                    <h3 className="text-lg font-black text-white">{selectedMatchDetail.team2?.name}</h3>
                    <span className="text-xs text-zinc-500 font-mono">Rank #{selectedMatchDetail.team2?.rank}</span>
                  </div>
                  <img src={selectedMatchDetail.team2?.logo} alt="t2" className="w-12 h-12 p-1.5 bg-zinc-900 rounded-xl" />
                </div>
              </div>

              {/* Prediction voting */}
              <div className="pt-4 border-t border-white/5 space-y-2">
                <div className="flex items-center justify-between text-xs text-zinc-400 font-bold">
                  <span>Predicții Comunitate: <strong>68% {selectedMatchDetail.team1?.tag}</strong></span>
                  <span><strong>32% {selectedMatchDetail.team2?.tag}</strong></span>
                </div>
                <div className="w-full h-2.5 rounded-full bg-zinc-800 overflow-hidden flex">
                  <div className="h-full bg-[var(--tf-primary)] w-[68%]" />
                  <div className="h-full bg-cyan-500 w-[32%]" />
                </div>
                <div className="flex items-center justify-center gap-3 pt-2">
                  <button
                    onClick={() => handlePredict(1)}
                    disabled={predicting}
                    className="px-4 py-1.5 rounded-lg bg-[var(--tf-primary)]/20 hover:bg-[var(--tf-primary)]/30 text-[var(--tf-primary)] border border-[var(--tf-primary)]/40 text-xs font-black uppercase flex items-center gap-1.5"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" /> Votează {selectedMatchDetail.team1?.tag}
                  </button>
                  <button
                    onClick={() => handlePredict(2)}
                    disabled={predicting}
                    className="px-4 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs font-black uppercase flex items-center gap-1.5"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" /> Votează {selectedMatchDetail.team2?.tag}
                  </button>
                </div>
              </div>
            </div>

            {/* Map Veto & Pick/Ban System */}
            <div className="space-y-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[var(--tf-primary)]" />
                Map Veto (Pick & Ban)
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs">
                {selectedMatchDetail.veto?.map((v, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-white text-[11px] truncate">{v.team}</span>
                      <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                        v.action === "banned" ? "bg-red-500/20 text-red-400" : (v.action === "picked" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400")
                      }`}>
                        {v.action}
                      </span>
                    </div>
                    <p className="font-mono text-zinc-300 text-xs">{v.map}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Scoreboard / Player Ratings */}
            <div className="space-y-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-[var(--tf-primary)]" />
                Statistici Jucători pe Hartă (Scoreboard & Rating 2.0)
              </h4>
              <div className="bg-black/60 border border-white/10 rounded-xl overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-white/5 border-b border-white/10 text-[10px] text-zinc-400 uppercase">
                    <tr>
                      <th className="p-3">Jucător</th>
                      <th className="p-3">Echipă</th>
                      <th className="p-3">K - D</th>
                      <th className="p-3">ADR</th>
                      <th className="p-3">KAST</th>
                      <th className="p-3 text-right">Rating 2.0</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {selectedMatchDetail.scoreboard?.map((p, idx) => (
                      <tr key={idx} className="hover:bg-white/5 transition-colors">
                        <td className="p-3 font-black text-white">{p.player}</td>
                        <td className="p-3 font-mono text-zinc-400">[{p.team}]</td>
                        <td className="p-3 font-mono">{p.kills} - {p.deaths}</td>
                        <td className="p-3 font-mono">{p.adr}</td>
                        <td className="p-3 font-mono">{p.kast}</td>
                        <td className="p-3 font-mono font-black text-right text-[var(--tf-primary)]">{p.rating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Live Stream or Commentary */}
            {selectedMatchDetail.stream_url && (
              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setActiveStreamUrl(selectedMatchDetail.stream_url)}
                  className="tf-btn-primary px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-red-600 text-white flex items-center gap-2"
                >
                  <Play className="w-4 h-4 fill-white" />
                  Deschide Transmisiunea Live
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TEAM ANALYTICS DRAWER / MODAL */}
      {selectedTeamTag && teamAnalytics && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-3xl bg-zinc-950 border border-white/20 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6 my-auto max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <img src={`https://api.dicebear.com/7.x/identicon/svg?seed=${teamAnalytics.name}`} alt="logo" className="w-12 h-12 rounded-xl bg-black p-1.5" />
                <div>
                  <h2 className="text-2xl font-black text-white">{teamAnalytics.name}</h2>
                  <span className="text-xs text-[var(--tf-primary)] font-mono font-bold">HLTV Rank #{teamAnalytics.rank} • {teamAnalytics.points} Points</span>
                </div>
              </div>
              <button onClick={() => { setSelectedTeamTag(null); setTeamAnalytics(null); }} className="p-1.5 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Map Winrates */}
            <div className="space-y-3">
              <h3 className="text-sm font-black text-white uppercase tracking-wider">Statistici pe Hărți (Map Winrates)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(teamAnalytics.map_winrates || {}).map(([map, winrate], idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <span className="text-[11px] font-mono text-zinc-400 uppercase block">{map}</span>
                    <span className="text-sm font-black text-[var(--tf-primary)]">{winrate}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Lineup Roles */}
            <div className="space-y-3">
              <h3 className="text-sm font-black text-white uppercase tracking-wider">Roster & Roluri Tactic</h3>
              <div className="divide-y divide-white/5 bg-black/60 border border-white/10 rounded-xl overflow-hidden">
                {teamAnalytics.roster_details?.map((p, idx) => (
                  <div key={idx} className="p-3 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3">
                      <img src={p.avatar} alt="av" className="w-8 h-8 rounded-lg bg-zinc-900" />
                      <div>
                        <p className="font-bold text-white">{p.name}</p>
                        <span className="text-[10px] text-zinc-500">{p.role}</span>
                      </div>
                    </div>
                    <span className="font-mono font-black text-[var(--tf-primary)]">Rating: {p.rating}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trophies */}
            <div className="space-y-2">
              <h3 className="text-sm font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                <Trophy className="w-4 h-4" /> Palmares & Trofee Majore
              </h3>
              <div className="flex flex-wrap gap-2">
                {teamAnalytics.trophies?.map((t, idx) => (
                  <span key={idx} className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold">
                    🏆 {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE SCRIM / CLAN WAR MODAL */}
      {showCreateScrimModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-lg bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-4 my-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                <Swords className="w-5 h-5 text-[var(--tf-primary)]" />
                Postează Meci de Antrenament / Scrim 5v5
              </h2>
              <button onClick={() => setShowCreateScrimModal(false)} className="p-1 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateScrim} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Nume Clan *</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: TaskForce Romania"
                    value={scrimClan}
                    onChange={e => setScrimClan(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Tag Clan</label>
                  <input
                    type="text"
                    placeholder="TFR"
                    value={scrimTag}
                    onChange={e => setScrimTag(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Joc</label>
                  <select
                    value={scrimGame}
                    onChange={e => setScrimGame(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                  >
                    <option value="cs2">Counter-Strike 2</option>
                    <option value="cs16">CS 1.6</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Hartă</label>
                  <input
                    type="text"
                    value={scrimMap}
                    onChange={e => setScrimMap(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Format</label>
                  <input
                    type="text"
                    value={scrimFormat}
                    onChange={e => setScrimFormat(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Data & Ora Meciului *</label>
                <input
                  type="datetime-local"
                  required
                  value={scrimDateTime}
                  onChange={e => setScrimDateTime(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                />
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Contact Discord / Username</label>
                <input
                  type="text"
                  placeholder="discord.gg/indungi / @Leader"
                  value={scrimDiscord}
                  onChange={e => setScrimDiscord(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowCreateScrimModal(false)} className="px-4 py-2 rounded-lg bg-white/10 text-white font-bold">
                  Anulează
                </button>
                <button type="submit" disabled={savingScrim} className="tf-btn-primary px-5 py-2 rounded-lg bg-[var(--tf-primary)] text-black font-black uppercase">
                  {savingScrim ? "Se publică..." : "Publică Meci 5v5"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STREAM VIDEO PLAYER MODAL */}
      {activeStreamUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-zinc-950 border border-white/20 rounded-2xl overflow-hidden shadow-2xl space-y-4 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold text-red-400 uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                TRANSMISIUNE LIVE INDUNGI ESPORTS
              </div>
              <button onClick={() => setActiveStreamUrl(null)} className="p-1 rounded bg-white/10 text-white hover:bg-white/20">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="aspect-video w-full rounded-xl overflow-hidden bg-black border border-white/10">
              {getStreamEmbed(activeStreamUrl) ? (
                <iframe
                  src={getStreamEmbed(activeStreamUrl).src}
                  title="Live Stream Player"
                  className="w-full h-full"
                  allowFullScreen
                  allow="autoplay; fullscreen"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center space-y-3">
                  <Video className="w-12 h-12 text-zinc-600" />
                  <a href={activeStreamUrl} target="_blank" rel="noreferrer" className="text-xs text-[var(--tf-primary)] underline font-bold">
                    Deschide transmisiunea în tab extern
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TOURNAMENT DETAILS & REAL BRACKET MODAL */}
      {selectedTournament && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-5xl bg-zinc-950 border border-white/20 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6 my-auto max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <span className="text-xs font-bold text-[var(--tf-primary)] uppercase tracking-wider">
                  INDUNGI REAL eSPORTS BRACKET ENGINE
                </span>
                <h2 className="text-2xl font-black text-white flex items-center gap-3">
                  <span>{selectedTournament.title}</span>
                  <span className={`text-[10px] px-2.5 py-0.5 rounded uppercase font-bold ${
                    selectedTournament.status === "in_progress"
                      ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                      : selectedTournament.status === "finished"
                      ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                      : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                  }`}>
                    {selectedTournament.status === "in_progress" ? "În Desfășurare" : selectedTournament.status === "finished" ? "Finalizat" : "Înscrieri"}
                  </span>
                </h2>
              </div>
              <button onClick={() => { setSelectedTournament(null); setTournamentBracket(null); }} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Champion Banner if finished */}
            {selectedTournament.champion && (
              <div className="bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent border border-amber-500/40 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/50 flex items-center justify-center text-amber-400">
                    <Trophy className="w-7 h-7" />
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-amber-400 tracking-widest block">Campioana Turneului</span>
                    <h3 className="text-lg font-black text-white">{selectedTournament.champion.name} [{selectedTournament.champion.tag}]</h3>
                  </div>
                </div>
                <span className="text-sm font-black text-amber-400 font-mono">🏆 {selectedTournament.prize_pool}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-1">
                <span className="text-zinc-500 font-bold uppercase">🏆 Premii Totale</span>
                <p className="text-base font-black text-amber-400">{selectedTournament.prize_pool}</p>
              </div>
              <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-1">
                <span className="text-zinc-500 font-bold uppercase">⚔️ Joc & Format</span>
                <p className="text-sm font-bold text-white uppercase">{selectedTournament.game} • {selectedTournament.format}</p>
              </div>
              <div className="bg-black/50 border border-white/10 rounded-xl p-4 space-y-1">
                <span className="text-zinc-500 font-bold uppercase">👥 Echipe Înscrise</span>
                <p className="text-sm font-bold text-white">{selectedTournament.enrolled_teams?.length || 0} / {selectedTournament.max_teams}</p>
              </div>
            </div>

            {/* REAL BRACKET VIEW */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                  <Swords className="w-4 h-4 text-[var(--tf-primary)]" />
                  Tablou Competițional Live (Single Elimination)
                </h3>
                {isStaff && selectedTournament.status === "registration" && (
                  <button
                    onClick={() => handleStartTournament(selectedTournament.id)}
                    disabled={startingTourn}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 text-white rounded-lg text-xs font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-500/20"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    {startingTourn ? "Se generează..." : "🚀 Pornește Turneul & Generează Bracket"}
                  </button>
                )}
              </div>

              {loadingBracket ? (
                <div className="p-12 text-center text-zinc-500 font-mono text-xs">Se încarcă schema competiției...</div>
              ) : tournamentBracket?.rounds && tournamentBracket.rounds.length > 0 ? (
                <div className="bg-black/60 border border-white/10 rounded-xl p-4 md:p-6 overflow-x-auto">
                  <div className="flex items-stretch min-w-[700px] gap-6">
                    {tournamentBracket.rounds.map((roundObj) => (
                      <div key={roundObj.round} className="flex-1 flex flex-col justify-around space-y-4">
                        <div className="text-center pb-2 border-b border-white/5">
                          <span className="text-xs font-black text-[var(--tf-primary)] uppercase tracking-wider block">
                            {roundObj.round_name}
                          </span>
                          <span className="text-[10px] text-zinc-500 font-mono">{roundObj.matches.length} meciuri</span>
                        </div>

                        <div className="space-y-4 flex-1 flex flex-col justify-around">
                          {roundObj.matches.map((m) => {
                            const isLive = m.status === "live" || m.status === "ready";
                            const isFinished = m.status === "finished";
                            const hasServer = m.server_ip && m.server_port;

                            return (
                              <div
                                key={m.id}
                                className={`rounded-xl p-3 space-y-2 border transition-all text-xs ${
                                  isLive
                                    ? "bg-zinc-900/90 border-cyan-500/50 shadow-lg shadow-cyan-500/10"
                                    : isFinished
                                    ? "bg-zinc-950/80 border-white/10"
                                    : "bg-black/40 border-white/5 opacity-80"
                                }`}
                              >
                                {/* Match Header */}
                                <div className="flex items-center justify-between text-[10px] text-zinc-500 pb-1 border-b border-white/5">
                                  <span>{m.map_name || "de_dust2"} • {m.format || "BO1"}</span>
                                  <span className={`font-bold uppercase ${isLive ? "text-cyan-400 animate-pulse" : isFinished ? "text-zinc-400" : "text-zinc-600"}`}>
                                    {isLive ? "● Pregătit" : isFinished ? "Finalizat" : "În Așteptare"}
                                  </span>
                                </div>

                                {/* Team 1 */}
                                <div className={`flex items-center justify-between p-1.5 rounded ${
                                  m.winner_id && m.winner_id === m.team1?.id ? "bg-emerald-500/10 font-bold text-emerald-400" : "text-white"
                                }`}>
                                  <div className="flex items-center gap-2 truncate">
                                    <span className="text-[10px] text-zinc-500 font-mono">[{m.team1?.tag || "TBD"}]</span>
                                    <span className="truncate">{m.team1?.name || "În Așteptare"}</span>
                                  </div>
                                  <span className="font-mono font-black text-sm ml-2">{m.team1_score ?? 0}</span>
                                </div>

                                {/* Team 2 */}
                                <div className={`flex items-center justify-between p-1.5 rounded ${
                                  m.winner_id && m.winner_id === m.team2?.id ? "bg-emerald-500/10 font-bold text-emerald-400" : "text-white"
                                }`}>
                                  <div className="flex items-center gap-2 truncate">
                                    <span className="text-[10px] text-zinc-500 font-mono">[{m.team2?.tag || "TBD"}]</span>
                                    <span className="truncate">{m.team2?.name || "În Așteptare"}</span>
                                  </div>
                                  <span className="font-mono font-black text-sm ml-2">{m.team2_score ?? 0}</span>
                                </div>

                                {/* Live Server Connect & Password */}
                                {hasServer && (
                                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                                    <div className="flex items-center justify-between text-[10px]">
                                      <span className="text-zinc-400">Server: {m.server_ip}:{m.server_port}</span>
                                      <code className="text-[var(--tf-primary)] bg-black/50 px-1.5 py-0.5 rounded font-mono">
                                        pass: {m.server_password}
                                      </code>
                                    </div>
                                    <a
                                      href={m.connect_url || `steam://connect/${m.server_ip}:${m.server_port}/${m.server_password}`}
                                      className="block w-full text-center py-1 bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-300 border border-cyan-500/40 rounded text-[10px] font-black uppercase tracking-wider transition-colors"
                                    >
                                      🎮 Conectare Server (Steam)
                                    </a>
                                  </div>
                                )}

                                {/* Admin / Captain Actions */}
                                {!isFinished && m.team1 && m.team2 && !m.team1.is_bye && !m.team2.is_bye && (
                                  <div className="pt-1 flex gap-1.5">
                                    <button
                                      onClick={() => handleCheckin(m.id)}
                                      className="flex-1 py-1 rounded bg-white/5 hover:bg-white/10 text-zinc-300 text-[10px] font-bold"
                                    >
                                      ✓ Check-in ({m.checkins?.length || 0})
                                    </button>
                                    {(isStaff || (user && (user.id === m.team1?.leader_id || user.id === m.team2?.leader_id))) && (
                                      <button
                                        onClick={() => {
                                          setReportingMatch(m);
                                          setReportScore1(m.team1_score || 16);
                                          setReportScore2(m.team2_score || 12);
                                          setShowReportModal(true);
                                        }}
                                        className="py-1 px-2.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-[10px] font-bold uppercase"
                                      >
                                        Raportează Scor
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center bg-black/40 border border-white/10 rounded-xl text-zinc-500 space-y-2">
                  <Swords className="w-8 h-8 mx-auto text-zinc-600" />
                  <p className="text-xs">Turneul este în faza de înscrieri. Bracket-ul va fi generat automat la pornirea meciurilor.</p>
                </div>
              )}
            </div>

            {/* Enrolled Teams List */}
            <div className="space-y-3">
              <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--tf-primary)]" />
                Echipe Înregistrate ({selectedTournament.enrolled_teams?.length || 0})
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {selectedTournament.enrolled_teams?.map((team, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2.5">
                    <img src={team.logo || `https://api.dicebear.com/7.x/identicon/svg?seed=${team.name}`} alt={team.name} className="w-8 h-8 rounded-lg bg-black/60 p-1" />
                    <div className="overflow-hidden">
                      <p className="text-xs font-bold text-white truncate">{team.name}</p>
                      <span className="text-[10px] text-zinc-400 font-mono">[{team.tag}]</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <span className="text-xs text-zinc-400">Servere dedicate: `209.38.247.243:27015-27017`</span>
              {selectedTournament.status === "registration" && (
                <button
                  onClick={() => handleRegisterTeam(selectedTournament.id)}
                  className="tf-btn-primary px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider bg-[var(--tf-primary)] text-black hover:opacity-90 transition-all shadow-lg"
                >
                  Înscrie Echipa Mea
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SCORE REPORT DIALOG */}
      {showReportModal && reportingMatch && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-base font-black text-white uppercase tracking-wider flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                Raportează Rezultat Meci
              </h3>
              <button onClick={() => setShowReportModal(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleReportScoreSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 p-3 rounded-xl space-y-2 text-center">
                  <span className="font-bold text-white block truncate">{reportingMatch.team1?.name || "Echipa 1"}</span>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={reportScore1}
                    onChange={e => setReportScore1(e.target.value)}
                    className="w-full bg-black border border-white/10 rounded p-2 text-center text-xl font-black text-white font-mono"
                    required
                  />
                </div>

                <div className="bg-white/5 p-3 rounded-xl space-y-2 text-center">
                  <span className="font-bold text-white block truncate">{reportingMatch.team2?.name || "Echipa 2"}</span>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={reportScore2}
                    onChange={e => setReportScore2(e.target.value)}
                    className="w-full bg-black border border-white/10 rounded p-2 text-center text-xl font-black text-white font-mono"
                    required
                  />
                </div>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300 text-[11px]">
                ⚠️ Câștigătorul va avansa automat în runda următoare a bracket-ului, iar serverul dedicat va fi configurat automat.
              </div>

              <button
                type="submit"
                disabled={savingReport}
                className="w-full py-2.5 bg-[var(--tf-primary)] text-black font-black uppercase tracking-wider rounded-lg text-xs hover:opacity-90 transition-opacity"
              >
                {savingReport ? "Se salvează..." : "Confirmă & Avansează Câștigătorul"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ADMIN CREATE TOURNAMENT MODAL */}
      {showCreateTournModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-xl bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-4 my-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[var(--tf-primary)]" />
                Creează Turneu Nou
              </h2>
              <button onClick={() => setShowCreateTournModal(false)} className="p-1 rounded text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTournament} className="space-y-3 text-xs">
              <div>
                <label className="block text-zinc-400 font-bold mb-1">Titlu Turneu *</label>
                <input
                  type="text"
                  required
                  placeholder="ex: Cupa INDUNGI 5v5 CS2..."
                  value={tTitle}
                  onChange={(e) => setTTitle(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Joc</label>
                  <select
                    value={tGame}
                    onChange={(e) => setTGame(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  >
                    <option value="cs2">Counter-Strike 2</option>
                    <option value="cs16">CS 1.6</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Premii</label>
                  <input
                    type="text"
                    value={tPrize}
                    onChange={(e) => setTPrize(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Max Echipe</label>
                  <input
                    type="number"
                    value={tMaxTeams}
                    onChange={(e) => setTMaxTeams(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Data Start</label>
                  <input
                    type="datetime-local"
                    required
                    value={tStartDate}
                    onChange={(e) => setTStartDate(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Descriere</label>
                <textarea
                  rows={3}
                  value={tDesc}
                  onChange={(e) => setTDesc(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)] resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowCreateTournModal(false)} className="px-4 py-2 rounded-lg bg-white/10 text-white font-bold">
                  Anulează
                </button>
                <button type="submit" disabled={savingTourn} className="tf-btn-primary px-5 py-2 rounded-lg bg-[var(--tf-primary)] text-black font-black uppercase">
                  {savingTourn ? "Se creează..." : "Creează Turneu"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADMIN CREATE MATCH MODAL */}
      {showCreateMatchModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-lg bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-4 my-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                <Swords className="w-5 h-5 text-[var(--tf-primary)]" />
                Programează Meci Oficial
              </h2>
              <button onClick={() => setShowCreateMatchModal(false)} className="p-1 rounded text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateMatch} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Echipa 1 (Nume) *</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Nexus Gaming"
                    value={mTeam1}
                    onChange={(e) => setMTeam1(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Tag Echipa 1</label>
                  <input
                    type="text"
                    placeholder="NEXUS"
                    value={mTeam1Tag}
                    onChange={(e) => setMTeam1Tag(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Echipa 2 (Nume) *</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: TaskForce Romania"
                    value={mTeam2}
                    onChange={(e) => setMTeam2(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Tag Echipa 2</label>
                  <input
                    type="text"
                    placeholder="TFR"
                    value={mTeam2Tag}
                    onChange={(e) => setMTeam2Tag(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Status</label>
                  <select
                    value={mStatus}
                    onChange={(e) => setMStatus(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  >
                    <option value="upcoming">Programat</option>
                    <option value="live">LIVE</option>
                    <option value="finished">Finalizat</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Hartă</label>
                  <input
                    type="text"
                    value={mMap}
                    onChange={(e) => setMMap(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">Format</label>
                  <input
                    type="text"
                    value={mFormat}
                    onChange={(e) => setMFormat(e.target.value)}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">YouTube / Twitch Stream URL</label>
                <input
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={mStream}
                  onChange={(e) => setMStream(e.target.value)}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white outline-none focus:border-[var(--tf-primary)]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowCreateMatchModal(false)} className="px-4 py-2 rounded-lg bg-white/10 text-white font-bold">
                  Anulează
                </button>
                <button type="submit" disabled={savingMatch} className="tf-btn-primary px-5 py-2 rounded-lg bg-[var(--tf-primary)] text-black font-black uppercase">
                  {savingMatch ? "Se programează..." : "Salvează Meci"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADMIN EDIT MATCH SCORE MODAL */}
      {showEditMatchModal && editingMatch && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-md bg-zinc-950 border border-white/20 rounded-2xl p-6 shadow-2xl space-y-4 my-auto">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h2 className="text-base font-black text-white">Editează Scor Meci</h2>
              <button onClick={() => setShowEditMatchModal(false)} className="p-1 text-zinc-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateMatchScore} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">{editingMatch.team1_name}</label>
                  <input
                    type="number"
                    value={editingMatch.team1_score}
                    onChange={(e) => setEditingMatch({ ...editingMatch, team1_score: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white text-lg font-black text-center"
                  />
                </div>
                <div>
                  <label className="block text-zinc-400 font-bold mb-1">{editingMatch.team2_name}</label>
                  <input
                    type="number"
                    value={editingMatch.team2_score}
                    onChange={(e) => setEditingMatch({ ...editingMatch, team2_score: e.target.value })}
                    className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white text-lg font-black text-center"
                  />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 font-bold mb-1">Status Meci</label>
                <select
                  value={editingMatch.status}
                  onChange={(e) => setEditingMatch({ ...editingMatch, status: e.target.value })}
                  className="w-full bg-black/60 border border-white/15 rounded-lg p-2.5 text-white"
                >
                  <option value="upcoming">Programat</option>
                  <option value="live">LIVE</option>
                  <option value="finished">Finalizat</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowEditMatchModal(false)} className="px-4 py-2 rounded-lg bg-white/10 text-white font-bold">
                  Anulează
                </button>
                <button type="submit" className="tf-btn-primary px-5 py-2 rounded-lg bg-[var(--tf-primary)] text-black font-black uppercase">
                  Actualizează Scor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
